import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, BookOpen, Loader2, ShoppingBag } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";

export default function BookletSample() {
    const { slug } = useParams();
    const [data, setData] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [buying, setBuying] = useState(false);
    const [email, setEmail] = useState("");

    useEffect(() => {
        try {
            const cached = localStorage.getItem("sd_buyer_email");
            if (cached) setEmail(cached);
        } catch (_) { /* ignore */ }
    }, []);

    useEffect(() => {
        setData(null);
        setNotFound(false);
        api.get(`/booklets/${slug}/sample`)
            .then((r) => setData(r.data))
            .catch((e) => {
                if (e.response?.status === 404) setNotFound(true);
            });
    }, [slug]);

    async function buy() {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
            toast.error("Please enter a valid email so we can deliver the booklet.");
            return;
        }
        setBuying(true);
        try {
            try { localStorage.setItem("sd_buyer_email", email.trim()); } catch (_) { /* ignore */ }
            const { data: resp } = await api.post("/booklets/checkout", {
                slug,
                email: email.trim(),
                origin_url: window.location.origin,
                currency: "NZD",
            });
            window.location.href = resp.url;
        } catch (err) {
            toast.error(
                formatApiErrorDetail(err.response?.data?.detail) ||
                    "Couldn't start checkout. Please try again.",
            );
            setBuying(false);
        }
    }

    if (notFound) {
        return (
            <div className="min-h-screen bg-page text-ink" data-testid="booklet-sample-notfound">
                <div className="mx-auto max-w-2xl px-5 py-24 text-center">
                    <div className="font-display text-4xl font-black">Booklet not found.</div>
                    <p className="mt-3 text-ink/70">The one you're after may have been unpublished.</p>
                    <Link
                        to="/"
                        className="mt-8 inline-flex items-center gap-2 border-2 border-ink bg-white px-5 py-3 font-display text-sm font-bold uppercase tracking-wider text-ink hover:border-coral hover:text-coral"
                    >
                        <ArrowLeft className="h-4 w-4" /> Back home
                    </Link>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="min-h-screen bg-page text-ink" data-testid="booklet-sample-loading">
                <div className="mx-auto max-w-2xl px-5 py-24 text-sm uppercase tracking-widest text-ink/45">
                    Loading sample…
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-page text-ink" data-testid="booklet-sample">
            {/* Coral top-band matching the printed booklet */}
            <div className="h-2 w-full bg-coral" />

            <div className="mx-auto max-w-3xl px-5 pb-24 pt-10 lg:px-10 lg:pt-16">
                <Link
                    to="/#booklets"
                    className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-ink/60 hover:text-coral"
                    data-testid="booklet-sample-back"
                >
                    <ArrowLeft className="h-4 w-4" /> All booklets
                </Link>

                {/* Cover */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-8 border-2 border-ink bg-white p-6 shadow-[8px_8px_0_0_#2D2D2D] lg:p-10"
                >
                    <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-coral">
                        Simple Digital · Free sample
                    </div>
                    <h1
                        className="mt-3 font-display text-4xl font-black leading-[0.95] tracking-tight text-ink sm:text-5xl lg:text-6xl"
                        data-testid="booklet-sample-title"
                    >
                        {data.title}
                    </h1>
                    {data.subtitle && (
                        <p className="mt-3 font-serif text-lg italic leading-snug text-ink/75 lg:text-xl">
                            {data.subtitle}
                        </p>
                    )}

                    {data.meta && data.meta.length > 0 && (
                        <div className="mt-6 border border-ink/20">
                            {data.meta.map((m, i) => (
                                <div
                                    key={m.label + i}
                                    className={`flex items-center justify-between gap-4 px-4 py-2 text-xs ${
                                        i > 0 ? "border-t border-ink/10" : ""
                                    }`}
                                >
                                    <span className="font-bold uppercase tracking-wider text-ink/70">
                                        {m.label}
                                    </span>
                                    <span className="text-ink/85">{m.value}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </motion.div>

                {/* Sample page — only show when there's actual content to preview */}
                {data.total_pages > 0 && (data.sample.heading || data.sample.intro || (data.sample.points && data.sample.points.length)) ? (
                    <motion.article
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                        className="mt-8 border-2 border-ink/15 bg-white p-6 lg:p-10"
                        data-testid="booklet-sample-body"
                    >
                        <div className="mb-4 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-coral">
                            <BookOpen className="h-3.5 w-3.5" /> Free sample · Page 1 of {data.total_pages}
                        </div>
                        {data.sample.heading && (
                            <h2 className="font-display text-2xl font-black leading-tight text-ink lg:text-3xl">
                                {data.sample.heading}
                            </h2>
                        )}
                        {data.sample.intro && (
                            <p className="mt-4 font-body text-base leading-relaxed text-ink/80 lg:text-lg">
                                {data.sample.intro}
                            </p>
                        )}

                        {data.sample.points && data.sample.points.length > 0 && (
                            <ul className="mt-6 space-y-3">
                                {data.sample.points.map((pt, i) => (
                                    <li key={i} className="flex gap-3 text-base leading-relaxed text-ink/90">
                                        <span className="mt-1 shrink-0 text-coral" aria-hidden>▸</span>
                                        <span>{pt}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </motion.article>
                ) : null}

                {/* Page-image preview — for booklets delivered as designed PDFs */}
                {data.preview_images && data.preview_images.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                        className="mt-8 border-2 border-ink/15 bg-white p-6 lg:p-10"
                        data-testid="booklet-sample-pages"
                    >
                        <div className="mb-4 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-coral">
                            <BookOpen className="h-3.5 w-3.5" /> Peek inside · first 2 pages
                        </div>
                        <div className="grid gap-4 sm:grid-cols-2">
                            {data.preview_images.slice(0, 2).map((u, i) => (
                                <img
                                    key={i}
                                    src={`${process.env.REACT_APP_BACKEND_URL}${u}`}
                                    alt={`${data.title} — page ${i + 1} preview`}
                                    loading="lazy"
                                    className="w-full border-2 border-ink/15 bg-white shadow-[4px_4px_0_0_#2D2D2D]"
                                    data-testid={`booklet-sample-page-${i + 1}`}
                                />
                            ))}
                        </div>
                    </motion.div>
                )}

                {/* CTA — single button, real price */}
                <div className="mt-8 space-y-3">
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Email for your download"
                        className="w-full border-2 border-ink/25 bg-white px-4 py-3 text-ink placeholder:text-ink/40 focus:border-coral focus:outline-none"
                        data-testid="booklet-sample-email"
                    />
                    <button
                        type="button"
                        onClick={buy}
                        disabled={buying}
                        className="inline-flex w-full items-center justify-center gap-2 border-2 border-coral bg-coral px-5 py-4 font-display text-sm font-bold uppercase tracking-wider text-white transition-all hover:-translate-y-0.5 disabled:opacity-60"
                        data-testid="booklet-sample-buy"
                    >
                        {buying ? (
                            <>Redirecting… <Loader2 className="h-4 w-4 animate-spin" /></>
                        ) : (
                            <><ShoppingBag className="h-4 w-4" /> Buy this booklet · ${(data.price ?? 9).toFixed(0)}</>
                        )}
                    </button>
                </div>

                <div className="mt-6 text-center text-[11px] uppercase tracking-widest text-ink/45">
                    Instant PDF · Secure Stripe checkout · NZD + GST
                </div>
            </div>
        </div>
    );
}
